// Org AI defaults — /org/settings/ai-defaults
//
// Admin-only. Writes to company_ai_preferences. RLS already restricts the
// table to owners/admins/platform-owners; we re-check role here so the page
// is hidden / shown a 403 card for editors and viewers.
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/hooks/useCompany";
import { ModelPicker } from "@/components/ai/ModelPicker";
import { getOrgPreferences, upsertOrgPreference, type TaskType } from "@/lib/credits/api";

const TASKS: TaskType[] = ["rewrite", "image", "translate", "qa", "seo", "score"];

export default function OrgAiDefaultsPage() {
  const { t } = useTranslation();
  const { currentCompanyId, currentCompany, role, isPlatformOwner } = useCompany();
  const qc = useQueryClient();
  const { toast } = useToast();

  const canEdit = isPlatformOwner || role === "owner" || role === "admin";

  const prefs = useQuery({
    queryKey: ["org-ai-prefs", currentCompanyId],
    enabled: !!currentCompanyId,
    queryFn: () => getOrgPreferences(currentCompanyId!),
  });

  const byTask = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of prefs.data ?? []) map.set(row.task_type, row.preferred_model_key);
    return map;
  }, [prefs.data]);

  async function setPrimary(task: TaskType, modelKey: string | null) {
    if (!currentCompanyId) return;
    try {
      await upsertOrgPreference(currentCompanyId, task, modelKey);
      toast({ description: t("models.orgSavedToast") });
      qc.invalidateQueries({ queryKey: ["org-ai-prefs", currentCompanyId] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    }
  }

  if (!currentCompanyId) {
    return <div className="container mx-auto py-8 text-muted-foreground">No active organisation.</div>;
  }
  if (!canEdit) {
    return (
      <div className="container mx-auto py-8 max-w-2xl">
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>Admin access required.</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (prefs.isLoading) {
    return (
      <div className="container mx-auto py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("featureGate.loading")}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold">
          {t("models.pickerTitle")} — {currentCompany?.name ?? "Organisation"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          These defaults apply to every site that has not set its own preference.
        </p>
      </div>

      {TASKS.map((task) => (
        <Card key={task}>
          <CardHeader>
            <CardTitle className="text-base">{t(`models.${task}`)}</CardTitle>
            <CardDescription className="text-xs">{t("models.pickerSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ModelPicker
              orgId={currentCompanyId}
              siteId={null}
              taskType={task}
              primary={byTask.get(task) ?? null}
              onPrimaryChange={(k) => setPrimary(task, k)}
              mode="org"
              showFallback={false}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
